/**
 * QuoteActionButtons — SendQuoteBtn, PreviewPdfBtn, ExportExecutionBtn.
 *
 * Phase 3h — Bug 10 & pattern "no action on stale state" :
 * chaque action accepte `onBeforeAction: () => Promise<void>` awaited AVANT l'API call.
 * Le parent (QuoteEditorPage) passe `saveNow` du hook `useQuoteDraftAutosave` →
 * l'action opère toujours sur l'état frais côté serveur.
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Eye, FileDown, Loader2, Mail } from "lucide-react";
import { ViewPdfLink } from "@/components/ViewPdfLink";

const DISABLED_BTN_CLASS = "border-slate-700 bg-slate-800/40 text-slate-400 opacity-70";

/** Envoi du devis au client (numérote + email + PDF + espace client). */
export function SendQuoteBtn({ quoteId, isDraft, quote, onSent, onBeforeAction, onChecklistBlocked }) {
  const [busy, setBusy] = useState(false);
  const canSend =
    isDraft && (quote.title || "").trim() && (quote.lines || []).some((l) => !l.is_note);

  const send = async () => {
    const nLines = (quote.lines || []).filter((l) => !l.is_note).length;
    const totalFmt = new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(
      quote.total_tvac || 0,
    );
    if (
      !window.confirm(
        `Envoyer ce devis au client ?\n\n` +
          `${nLines} ligne(s) facturable(s) — Total TVAC ${totalFmt}.\n\n` +
          `Un numéro séquentiel sera attribué et un email envoyé avec le PDF en pièce jointe.`,
      )
    )
      return;
    setBusy(true);
    try {
      if (onBeforeAction) await onBeforeAction();
      const { data } = await api.post(`/quotes/${quoteId}/send`);
      toast.success(`Devis ${data.number} envoyé au client. Espace client ouvert dans un nouvel onglet.`);
      if (data.portal_url) window.open(data.portal_url, "_blank", "noopener");
      onSent?.(data);
    } catch (e) {
      // Phase 4c — Interception blocage checklist : detail = {message, missing_item_ids}
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 400 &&
        detail &&
        typeof detail === "object" &&
        Array.isArray(detail.missing_item_ids)
      ) {
        onChecklistBlocked?.(detail.missing_item_ids);
        toast.error("Envoi bloqué — checklist incomplète", {
          description: detail.message || `${detail.missing_item_ids.length} point(s) à traiter avant l'envoi.`,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!isDraft) return null;
  if (!canSend) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button variant="outline" disabled className={DISABLED_BTN_CLASS} data-testid="quote-send-email-btn">
                <Mail className="mr-2 h-4 w-4" /> Envoyer par email
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="border border-slate-700 bg-slate-900 text-slate-100">
            {!(quote.title || "").trim()
              ? "L'intitulé est obligatoire avant envoi"
              : "Ajoutez au moins une ligne facturable"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <Button
      onClick={send}
      disabled={busy}
      className="text-white"
      style={{ backgroundColor: "#7A8450" }}
      data-testid="quote-send-email-btn"
    >
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
      Envoyer par email
    </Button>
  );
}

/** Prévisualise le PDF variant `client`.
 * BLOC 2026-10-18 20h55 — Refonte iPhone-safe : pré-fetch href + `<a>` natif.
 * Zéro window.open, zéro createObjectURL. onBeforeAction supprimé (save
 * doit être fait AVANT le clic sur le bouton par le parent).
 */
export function PreviewPdfBtn({ quoteId, disabled }) {
  return (
    <ViewPdfLink
      entityType="quote"
      entityId={quoteId}
      enabled={!disabled}
      label="Prévisualiser PDF"
      testid="quote-preview-pdf-btn"
      as="button-outline"
      disabled={disabled}
    />
  );
}

/** Télécharge la version chantier (sans montants). Désactivé sur brouillon. */
export function ExportExecutionBtn({ quoteId, isDraft, onBeforeAction }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      if (onBeforeAction) await onBeforeAction();
      const res = await api.get(`/quotes/${quoteId}/pdf?variant=execution`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `devis-version-chantier.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  if (isDraft) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                variant="outline"
                disabled
                className={DISABLED_BTN_CLASS}
                data-testid="quote-export-execution-btn"
              >
                <FileDown className="mr-2 h-4 w-4" /> Version chantier
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="border border-slate-700 bg-slate-900 text-slate-100">
            Disponible après envoi du devis
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <Button
      onClick={download}
      disabled={busy}
      variant="outline"
      className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
      data-testid="quote-export-execution-btn"
    >
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
      Version chantier
    </Button>
  );
}
